import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AccessLevel } from 'app/models/permission.model';
import { PermissionService } from './permission.service';
import { StartPageService } from './start-page.service';

/**
 * Keeps the open page in line with the permission map. PermissionGuard checks a page only when it
 * is opened; when the map changes afterwards (a 403 reloads it after an administrator changed the
 * role), the page the user is on may no longer be allowed. Then the user is sent to their start page,
 * as the guard would have done.
 */
@Injectable({ providedIn: 'root' })
export class PageAccessWatcher {
  private started = false;

  constructor(
    private router: Router,
    private permissions: PermissionService,
    private startPage: StartPageService
  ) { }

  /** Start watching; called once when the Portal starts */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.permissions.changes.pipe(filter(grants => grants !== null)).subscribe(() => this.recheck());
  }

  /** Leave the open page when a route on its path asks for an area level the user no longer has */
  recheck(): void {
    if (this.router.getCurrentNavigation()) return;   // PermissionGuard checks pages being opened

    for (let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root; route; route = route.firstChild) {
      const area: string | undefined = route.routeConfig?.data?.area;
      const access: AccessLevel = route.routeConfig?.data?.access ?? 'Read';
      if (area && !this.permissions.has(area, access)) {
        this.router.navigateByUrl(this.startPage.startPage());
        return;
      }
    }
  }
}
